import type { ReactNode } from 'react'

// The §15.1 modal stat tile — extracted verbatim from
// PersonDeepDiveModal.tsx's `Kpi` (lines 32-46: dense flat KPI, NOT GlassKpi
// — that's the hero-dashboard variant). Dense KPI, no card ever taller than
// its siblings (DESIGN.md §15.1). `dotColor`'s inline style becomes a static
// `dotClassName` here; `tone` gains 'neutral'/'warn' alongside the
// original's 'success'/'danger'; `hintMono` and `onClick` are new.
// Label and hint use text-on-surface-variant instead of the original's
// slate-400: slate-400 on white is ~2.6:1, below WCAG AA (Astra 23.09.2026).
// Tone colours carry the repo's usual 700 / dark:300 pair.

const TONE_VALUE = {
  neutral: 'text-slate-900 dark:text-on-surface',
  success: 'text-emerald-700 dark:text-emerald-300',
  warn: 'text-amber-700 dark:text-amber-300',
  danger: 'text-rose-700 dark:text-rose-300',
} as const

const TILE_BASE = 'rounded-lg border border-slate-200 bg-white/70 px-2.5 py-2 dark:border-outline-variant/40 dark:bg-surface-container-lowest/70'
const TILE_INTERACTIVE = 'rounded-lg border border-slate-200 bg-white/70 px-2.5 py-2 dark:border-outline-variant/40 dark:bg-surface-container-lowest/70 text-left w-full hover:border-outline-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30'
const HINT_BASE = 'mt-1 text-[10px] leading-tight text-on-surface-variant'
// No repo precedent for a Tailwind JetBrains Mono utility (only ever
// hardcoded via CSS `font:` shorthand in index.css) — arbitrary value class.
const HINT_MONO = "mt-1 text-[10px] leading-tight text-on-surface-variant font-['JetBrains_Mono',monospace]"

interface StatTileProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  /** A STATIC bg-* class, e.g. "bg-emerald-700" — never assembled from a variable part. */
  dotClassName?: string
  tone?: keyof typeof TONE_VALUE
  /** Renders `hint` in JetBrains Mono. */
  hintMono?: boolean
  /** When set, the tile becomes a `<button type="button">` (the only place
   *  this shared primitive uses a native button per DESIGN.md §8c). */
  onClick?: () => void
}

export function StatTile({ label, value, hint, dotClassName, tone = 'neutral', hintMono = false, onClick }: StatTileProps) {
  const rootClassName = onClick ? TILE_INTERACTIVE : TILE_BASE
  const content = (
    <>
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">
        {dotClassName && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClassName}`} />}
        {label}
      </div>
      <div className={`mt-0.5 text-xl font-semibold leading-none tabular-nums ${TONE_VALUE[tone]}`}>{value}</div>
      {hint && <div className={hintMono ? HINT_MONO : HINT_BASE}>{hint}</div>}
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={rootClassName}>
        {content}
      </button>
    )
  }
  return <div className={rootClassName}>{content}</div>
}
